import type { RefObject } from "react";
import { useCallback, useRef } from "react";

import { colorsPalettes } from "@similarweb/styles";

import { PdfExportService } from "app/services/PdfExportService";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";

import type { IServices } from "../../common/useMarketingChannelsServices";
import { resolveCurrentCategory, resolveCurrentDuration } from "../../common/utils/marketingChannelsApiHelper";

const PNG_OFFSET_X = 0;
const PNG_OFFSET_Y = 50;

const getPngFileName = (services: IServices) => {
    const category = resolveCurrentCategory(services);
    const duration = resolveCurrentDuration(services);
    return `Traffic Volumes By Source-(${category.forDisplayApi})-(${duration.forWidget})`;
};

export const usePngExport = (services: IServices) => {
    const widgetRef = useRef(null);

    const handlePNGExport = useCallback((): Promise<void> => {
        const styleHTML = PdfExportService.getStylesheetsForPng();

        const pngFileName = getPngFileName(services);
        const pngHtml = styleHTML + widgetRef.current.outerHTML;
        const pngWidth = widgetRef.current.offsetWidth + PNG_OFFSET_X;
        const pngHeight = widgetRef.current.offsetHeight + PNG_OFFSET_Y;

        return services.pdfService.downloadHtmlPngFedService(pngHtml, pngFileName, pngWidth, pngHeight);
    }, [widgetRef, services]);

    return {
        handlePNGExport,
        widgetRef,
    };
};

const createColoredLegend = (
    legendContainer: Element,
    categories: ICategoryComparison[],
    originalLegendItems: string[],
): void => {
    legendContainer.innerHTML = "";

    const legendWrapper = document.createElement("div");
    legendWrapper.style.cssText = `
        display: flex;
        flex-direction: column;
        padding: 20px;
        font-family: Roboto, sans-serif;
        font-size: 14px;
    `;

    // Create custom headers
    const headerRow = document.createElement("div");
    headerRow.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
    `;

    const industryHeader = document.createElement("span");
    industryHeader.textContent = "Industry";
    industryHeader.style.cssText = `
        color: ${colorsPalettes.carbon[400]};
        font-weight: 600;
        font-size: 11px;
    `;

    const volumeHeader = document.createElement("span");
    volumeHeader.textContent = "Volume";
    volumeHeader.style.cssText = `
        color: ${colorsPalettes.carbon[400]};
        font-weight: 600;
        font-size: 11px;
    `;

    headerRow.appendChild(industryHeader);
    headerRow.appendChild(volumeHeader);
    legendWrapper.appendChild(headerRow);

    // Create legend items with colored dots
    categories.forEach((category) => {
        const matchingText =
            originalLegendItems.find((text) => text && text.includes(category.category.forDisplayApi)) ||
            category.category.forDisplayApi;

        const legendItem = document.createElement("div");
        legendItem.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
            width: 100%;
        `;

        const leftContainer = document.createElement("div");
        leftContainer.style.cssText = `
            display: flex;
            align-items: center;
            gap: 12px;
        `;

        const colorDot = document.createElement("span");
        colorDot.style.cssText = `
            display: inline-block;
            width: 16px;
            height: 16px;
            background-color: ${category.color};
            border-radius: 50%;
            flex-shrink: 0;
        `;

        const parts = matchingText.split(/(\d[\d,]*\.?\d*[KMBT]?)/);
        const categoryName = parts[0]?.trim() || category.category.forDisplayApi;
        const volumeValue = parts.slice(1).join("").trim();

        const categorySpan = document.createElement("span");
        categorySpan.textContent = categoryName;
        categorySpan.style.cssText = `
            color: ${colorsPalettes.carbon[400]};
            font-weight: 500;
            line-height: 1.4;
        `;

        const volumeSpan = document.createElement("span");
        volumeSpan.textContent = volumeValue || "";
        volumeSpan.style.cssText = `
            color: ${colorsPalettes.carbon[400]};
            font-weight: 400;
            font-size: 12px;
            line-height: 1.4;
        `;

        leftContainer.appendChild(colorDot);
        leftContainer.appendChild(categorySpan);

        legendItem.appendChild(leftContainer);
        if (volumeValue) {
            legendItem.appendChild(volumeSpan);
        }

        legendWrapper.appendChild(legendItem);
    });

    legendContainer.appendChild(legendWrapper);
};

export const exportMarketingChannelsComparisonPng = (
    containerRef: RefObject<HTMLDivElement>,
    categories: ICategoryComparison[],
): Promise<void> => {
    if (!containerRef.current) {
        return Promise.resolve();
    }

    const styleHTML = PdfExportService.getStylesheetsForPng();

    const clonedChart = containerRef.current.cloneNode(true) as HTMLDivElement;

    const legendHeaders = clonedChart.querySelectorAll('[data-automation="graph-legend-headers"]');
    legendHeaders.forEach((header) => header.remove());

    const legendContainer = clonedChart.querySelector(".legends-container");

    if (legendContainer) {
        const originalLegendItems = Array.from(legendContainer.querySelectorAll("*"))
            .map((el) => el.textContent)
            .filter((text) => text && text.trim().length > 0);

        createColoredLegend(legendContainer, categories, originalLegendItems);
    }

    return PdfExportService.downloadHtmlPngFedService(
        styleHTML + clonedChart.outerHTML,
        "Channel Traffic",
        containerRef.current.offsetWidth + PNG_OFFSET_X,
        containerRef.current.offsetHeight + PNG_OFFSET_Y,
    );
};
